import React, { useState, useEffect } from 'react';
import IDCUModal from './IDCUModal';
import PadlockComponent from './PadlockComponent';
import audioManager from '../../utils/filemanager/AudioManager';
import DocumentFetcher from '../../utils/filemanager/DocumentFetcher';
import PlayerService from '../../services/PlayerService';
import accountStore from '../account/AccountStore';
import '../../App.css';

const SplashScreen = ({ onLoadingComplete, showStartButton, onStartScanning }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check authentication status once on mount and when URL changes
  useEffect(() => {
    const checkAuth = async () => {
      
      const authStatus = await PlayerService.isAuthenticated();
      
      setIsAuthenticated(authStatus);
    };
    
    checkAuth();
  }, [window.location.href]); // Only recheck when URL changes (after auth redirect)

  // Subscribe to AccountStore changes
  useEffect(() => {
    const unsubscribe = accountStore.subscribe(profile => {
      setIsAuthenticated(!!profile?.data);
    });
    return unsubscribe;
  }, []);
  const [currentText, setCurrentText] = useState('');
  const [currentLine, setCurrentLine] = useState(0);
  const [glitchEffect, setGlitchEffect] = useState(false);
  const [showButton, setShowButton] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showPadlock, setShowPadlock] = useState(false);

  const bootSequence = [
    { text: 'INITIALIZING', delay: 300 },
    { text: 'CONNECTED', delay: 200 },
    { text: 'READY', delay: 0 }
  ];

  const isDevelopment = window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1';


  // Initialize audio system
  useEffect(() => {
    audioManager.init().catch(console.error);
  }, []);

  // Handle typing animation
  useEffect(() => {
    let currentChar = 0;
    let timer;
    
    const typeText = () => {
      if (currentLine >= bootSequence.length) {
        if (showStartButton) {
          setShowButton(true);
        } else {
          onLoadingComplete();
        }
        return;
      }

      const currentMessage = bootSequence[currentLine].text;
      
      if (currentChar <= currentMessage.length) {
        setCurrentText(currentMessage.substring(0, currentChar));
        currentChar++;
        timer = setTimeout(typeText, 20);
      } else {
        // Clear text before moving to next line
        timer = setTimeout(() => {
          setCurrentText('');
          setCurrentLine(prev => prev + 1);
        }, bootSequence[currentLine].delay);
      }
    };

    timer = setTimeout(typeText, 10);
    return () => clearTimeout(timer);
  }, [currentLine, onLoadingComplete, showStartButton]);

  // Handle glitch effect
  useEffect(() => {
    const glitchInterval = setInterval(() => {
      if (Math.random() < 0.2) {
        setGlitchEffect(true);
        setTimeout(() => setGlitchEffect(false), 150);
      }
    }, 2000);

    return () => clearInterval(glitchInterval);
  }, []);

  const handleInitiate = () => {
    audioManager.playAlert().catch(console.error);
    setGlitchEffect(true);

    // Skip padlock in development environment
    if (isDevelopment) {
      setTimeout(() => {
        setGlitchEffect(false);
        handleUnlock();
      }, 150);
    } else {
      setTimeout(() => {
        setGlitchEffect(false);
        setShowPadlock(true);
      }, 150);
    }
  };

  const handleUnlock = () => {
    let hasVisited = false;

    // Try to check localStorage, but don't rely on it
    try {
      hasVisited = localStorage.getItem('neuranet_visited') === 'true';
    } catch (error) {
      // localStorage not available (e.g., incognito mode)

    }

    if (!hasVisited) {
      audioManager.playSelect().catch(console.error);
      setShowPadlock(false); // Hide padlock before showing modal
      setIsModalOpen(true);
    } else {
      // Skip modal for returning users
      audioManager.playSuccess().catch(console.error);
      setShowPadlock(false); // Hide padlock before proceeding
      onStartScanning();
    }
  };

  const handleModalClose = () => {
    // Try to mark user as having visited, but don't block on failure
    try {
      localStorage.setItem('neuranet_visited', 'true');
    } catch (error) {
      // localStorage not available (e.g., incognito mode)

    }

    audioManager.playSuccess().catch(console.error);
    setIsModalOpen(false);
    onStartScanning();
  };

  const handleButtonHover = () => {
    audioManager.playHover().catch(console.error);
  };

  // Render modal regardless of other component states
  const renderModal = () => {
    if (isModalOpen) {
      return <IDCUModal isOpen={true} onClose={handleModalClose} />;
    }
    return null;
  };

  // Render padlock if active
  if (showPadlock) {
    return (
      <>
        <PadlockComponent
          onUnlock={handleUnlock}
          isAuthenticated={isAuthenticated}
        />
        {renderModal()}
      </>
    );
  }

  // Render main splash screen
  return (
    <div className={`splash-screen ${glitchEffect ? 'glitch' : ''}`}>
      <div className="crt-overlay"></div>
      <img
        src={`${process.env.PUBLIC_URL}/IDCU_svg.svg`}
        alt="IDCU Logo"
        style={{
          width: '200px',
          marginBottom: '20px',
          cursor: 'pointer',
          transition: 'transform 0.2s ease-in-out',
          transform: glitchEffect ? 'scale(1.05)' : 'scale(1)'
        }}
        onMouseEnter={() => setGlitchEffect(true)}
        onMouseLeave={() => setGlitchEffect(false)}
        onClick={() => DocumentFetcher.openDocument('IDCU_DIRECTIVE.jpg')}
      />
      <div className="boot-header">
        NEURANET
        <div className="boot-subheader">[NEURAL:CONVERGENCE:PROTOCOL] //0xΨ∞</div>
      </div>
      <div className="boot-sequence">
        {currentLine < bootSequence.length && (
          <div className="boot-line typing">
            {'>'} {currentText}
          </div>
        )}
        {showButton && (
          <div className="start-button-container">
            <button
              className="start-button"
              onClick={handleInitiate}
              onMouseEnter={handleButtonHover}
              onMouseLeave={() => setGlitchEffect(false)}
            >
              {'['}INITIATE{']'}
            </button>
          </div>
        )}
      </div>
      {renderModal()}
    </div>
  );
};

export default SplashScreen;